import api from '../api/client';
import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  message,
  Spin,
} from "antd";
import axios from "axios";
import { bankData } from "../utils/banks";
import config from "../config";

const { Option } = Select;

const EmployeeDetailsForm = ({ userId, visible, onCancel, isEdit = false, existingData = null, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const environment = window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;

  // Fetch users without details
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get(`/users-without-details`);
        const options = data.map((u) => ({
          value: u.id,
          label: `${u.firstname} ${u.lastname}`,
        }));
        setUsers(options);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, []);

  // Set form values when editing
  useEffect(() => {
    if (isEdit && existingData) {
      form.setFieldsValue({
        user_id: existingData.user_id,
        base_salary: existingData.base_salary,
        bank_name: existingData.bank_name,
        bank_account: existingData.bank_account,
        branch_code: existingData.branch_code,
        branch_name: existingData.branch_name,
        account_type: existingData.account_type,
        kra_pin: existingData.kra_pin,
        nssf_number: existingData.nssf_number,
        nhif_number: existingData.nhif_number,
        payment_method: existingData.payment_method || "Bank",
        notes: existingData.notes,
      });

      // Load branches if bank name exists
      if (existingData.bank_name) {
        const selectedBank = bankData.find((b) => b.name === existingData.bank_name);
        setBranches(selectedBank ? selectedBank.branches : []);
      }
    } else {
      form.resetFields();
      form.setFieldsValue({ payment_method: "Bank" });
    }
  }, [isEdit, existingData, form]);

  const handleBankChange = (value) => {
    form.setFieldsValue({ bank_name: value, branch_name: undefined, branch_code: undefined });
    const selectedBank = bankData.find((b) => b.name === value);
    setBranches(selectedBank ? selectedBank.branches : []);
  };

  const handleBranchChange = (value) => {
    const selected = branches.find((b) => b.name === value);
    if (selected) {
      form.setFieldsValue({ branch_name: value, branch_code: selected.code });
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/employee-details/${existingData.user_id}`, values);
        message.success("Employee details updated successfully");
      } else {
        await api.post(`/employee-details`, values);
        message.success("Employee details saved successfully");
      }
      onSuccess();
      onCancel();
    } catch (error) {
      message.error(error.response?.data?.message || `Error ${isEdit ? "updating" : "saving"} details`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={isEdit ? "Edit Employee Details" : "Add Employee Details"}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="user_id" label="Employee" rules={[{ required: true }]}>
                <Select
                  placeholder="Select employee"
                  options={users}
                  disabled={isEdit}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="base_salary" label="Base Salary" rules={[{ required: true }]}>
                <Input type="number" min={0} step={1000} placeholder="Enter base salary" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="bank_name" label="Bank">
                <Select
                  placeholder="Select bank"
                  allowClear
                  showSearch
                  onChange={handleBankChange}
                  optionFilterProp="children"
                >
                  {bankData.map((bank) => (
                    <Option key={bank.name} value={bank.name}>
                      {bank.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="branch_name" label="Bank Branch">
                <Select
                  placeholder="Select branch"
                  allowClear
                  showSearch
                  onChange={handleBranchChange}
                  disabled={branches.length === 0}
                  optionFilterProp="children"
                >
                  {branches.map((branch) => (
                    <Option key={branch.name} value={branch.name}>
                      {branch.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="branch_code" label="Branch Code">
                <Input readOnly />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="bank_account" label="Bank Account">
                <Input placeholder="Enter bank account number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="account_type" label="Account Type">
                <Select placeholder="Select account type" allowClear>
                  <Option value="Savings">Savings</Option>
                  <Option value="Current">Current</Option>
                  <Option value="Fixed Deposit">Fixed Deposit</Option>
                  <Option value="Business">Business</Option>
                  <Option value="Salary">Salary</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="payment_method" label="Payment Method" rules={[{ required: true }]}>
                <Select>
                  <Option value="Bank">Bank Transfer</Option>
                  <Option value="Mpesa">M-Pesa</Option>
                  <Option value="Cash">Cash</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="kra_pin" label="KRA PIN">
                <Input placeholder="Enter KRA PIN" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="nssf_number" label="NSSF Number">
                <Input placeholder="Enter NSSF number" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="nhif_number" label="NHIF Number">
                <Input placeholder="Enter NHIF number" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} placeholder="Additional notes about the employee" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              {isEdit ? "Update" : "Submit"}
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={onCancel}>
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default EmployeeDetailsForm;